import { useState } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';

import JobTaskListView from '@/components/tasks/list';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { selectJobOverviewData, selectTasksByJobId, selectCheckinByBooking, selectPhotosByJobId } from '@/store/selectors';
import { addJobState, updateJobState } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';

export const Route = createFileRoute('/job/$jobId/$date/photos')({
  component: () => (
    <ProtectedRoute>
      <JobPhotosPage />
    </ProtectedRoute>
  ),
});

interface PhotoItem {
  id?: string;
  local: string;
  tag: string;
  task_id: string;
  job_id: string;
  [key: string]: any;
}

function JobPhotosPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/photos' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showAllPhotos, setShowAllPhotos] = useState(false);

  // Get job data from Redux
  const { booking, job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  const checkinToday = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  // Get tasks for this job
  const tasks = useSelector((state: RootState) => selectTasksByJobId(state, jobId));

  // Get photos for this job, filtered by tag
  const allPhotos = useSelector((state: RootState) => selectPhotosByJobId(state, jobId));

  const preliminaryPhotos = allPhotos.filter((photo: PhotoItem) => photo.tag === 'preliminary');
  const progressPhotos = allPhotos.filter((photo: PhotoItem) => photo.tag === 'progress');
  const completedPhotos = allPhotos.filter((photo: PhotoItem) => photo.tag === 'completed');

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const handleUpdateJobState = () => {
    if (typeof jobState !== 'undefined') {
      dispatch(updateJobState({
        ...jobState,
        confirmed_protective_photos: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    } else {
      dispatch(addJobState({
        job_id: job.id,
        confirmed_protective_photos: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    }

    // Navigate to next step (checkout)
    navigate({ to: '/job/$jobId/$date/checkout', params: { jobId, date } });
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  const isPhotosComplete = jobState?.confirmed_protective_photos !== null;

  return (
    <>
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4 justify-start">
            <h1 className="text-3xl font-bold leading-tight m-0">
              Photos
            </h1>

            <p className="text-base leading-relaxed mt-4 mb-4">
              During your work please provide us with any photos that will help us to identify and resolve issues. For example, if you do one thing and that leads to something else being required, please provide photo evidence of it.
            </p>
          </div>

          {/* Tasks Section */}
          <div className="space-y-4">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              Tasks
            </Label>
            <JobTaskListView
              canAddPhotos={true}
              canUpdateTimesheet={true}
              updateJobStatus={true}
              checkin={checkinToday}
              tasks={tasks}
            />

            {/* View All Photos Link */}
            <div className="mt-4 flex items-start">
              <button
                onClick={() => setShowAllPhotos(true)}
                className="flex items-center mt-1 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Label className="m-0 text-blue-600 cursor-pointer hover:text-blue-800">
                  View all photos for this Job
                </Label>
              </button>
            </div>
          </div>

          {/* Photo Summary Stats (Optional) */}
          {allPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-4 py-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{preliminaryPhotos.length}</p>
                <p className="text-sm text-gray-600">Preliminary</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{progressPhotos.length}</p>
                <p className="text-sm text-gray-600">In Progress</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{completedPhotos.length}</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 py-8 border-t border-gray-200">
            {isPhotosComplete ? (
              <Button
                onClick={handleGoBack}
                className="flex-1"
              >
                Back to Overview
              </Button>
            ) : (
              <Button
                onClick={handleUpdateJobState}
                className="flex-1"
                disabled={allPhotos.length === 0}
              >
                Continue to Check Out
              </Button>
            )}

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>
        </div>
      </div>

      {/* All Photos Modal */}
      <Dialog open={showAllPhotos} onOpenChange={setShowAllPhotos}>
        <DialogContent className="max-w-6xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>All Photos for Job</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="space-y-6">

                {/* Preliminary Photos */}
                {preliminaryPhotos.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-blue-600">
                      Preliminary Photos ({preliminaryPhotos.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {preliminaryPhotos.map((photo, index) => (
                        <div key={index} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500 text-sm">Photo {index + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Progress Photos */}
                {progressPhotos.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-amber-600">
                      Progress Photos ({progressPhotos.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {progressPhotos.map((photo, index) => (
                        <div key={index} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500 text-sm">Photo {index + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completed Photos */}
                {completedPhotos.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-3 text-green-600">
                      Completed Photos ({completedPhotos.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {completedPhotos.map((photo, index) => (
                        <div key={index} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500 text-sm">Photo {index + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Photos Message */}
                {allPhotos.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 opacity-50">
                      <svg
                        className="w-full h-full text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-500">No photos have been added yet</p>
                  </div>
                )}

              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>
    </ScrollArea>
</>
  );
}
